import type { CartRemoval, CashMovement, CashMovementReason, CustomerPayment, ExpenseCategory, PriceChange, RecurringExpense, PrepaidMove, RemovalKind, Sale, Shift, SupplierPayment } from './mock-data'
import { CARD_PROVIDER_LABELS, saleAmount } from './mock-data'
import { cashTerms, expectedCash, outstandingDebt, saleRails, sumRails } from './cash'
import { takenAtClose } from './drawer-float'
import type { OwnerReport } from './owner-report'
import { groupPriceChanges, groupRemovals } from './loss-groups'
import { reminderText, waPhone, whatsAppLink } from './debt-reminder'
import { comingUp, waitingForAmount } from './recurring-due'
import type { WeekReport } from './week-report'
import type { ExchangeShiftTotals } from './exchange'
import { LOW_STOCK } from './intelligence'
import { common as kaCommon } from './i18n/ka/common'
import { common as ruCommon } from './i18n/ru/common'

/**
 * The owner's Telegram (123, 2026-09-30) — what the bot says, in Georgian or
 * Russian. Pure: the route (app/api/telegram) fetches the rows, the app's own
 * functions count (`cashTerms`/`expectedCash` — term for term `close_shift()`;
 * `saleRails` — the money columns; `buildOwnerReport` — the morning report),
 * and this module only phrases what they returned. A second formula here would
 * be the next „two screens, two numbers".
 *
 * The look (the owner, same day: „უფრო ლამაზი იყოს"): Telegram gives a bot
 * no colour and no font size, so a message is built from what it does give —
 * <b>, <i>, and <blockquote>, which Telegram draws as a card with a coloured
 * bar (`expandable` folds a long one). Colour exists only as emoji: 🟢 exact,
 * 🔴 short, 🟡 over. The chat carries a menu keyboard (📊 💵 🌅 📦) so nobody
 * types commands, and the live messages carry „🔄 განახლება", which edits the
 * message in place.
 */

export type TgLang = 'ka' | 'ru'

/** What the bot pushes on its own — each one a per-shop switch (settings KV). */
export const TG_KINDS = ['morning', 'shift', 'evening', 'sale', 'loss', 'week', 'waybill', 'order'] as const
export type TgKind = (typeof TG_KINDS)[number]
export const TG_SETTING_KEY: Record<TgKind, string> = { morning: 'tgMorning', shift: 'tgShift', evening: 'tgEvening', sale: 'tgSale', loss: 'tgLoss', week: 'tgWeek', waybill: 'tgWaybill', order: 'tgOrder' }
/** The owner's choice, 2026-09-30: the three reports ON, every sale OFF; loss control and the Monday week ON (his picks of the extras).
 *  A new RS.GE waybill (2026-10-01) ON — it only ever reaches a shop that connected RS.GE.
 *  A new online order (B2, 2026-10-06) ON — it only ever reaches a shop with a web store, and a new
 *  order holds its stock until the shop answers (152), so the shop must hear of it at once. */
export const TG_DEFAULTS: Record<TgKind, boolean> = { morning: true, shift: true, evening: true, sale: false, loss: true, week: true, waybill: true, order: true }
export function tgEnabled(kv: Record<string, string | undefined>, kind: TgKind): boolean {
  const v = kv[TG_SETTING_KEY[kind]]
  return v === 'true' ? true : v === 'false' ? false : TG_DEFAULTS[kind]
}
/** The owner and one or two people he chooses (2026-09-30). */
export const TG_MAX_CHATS = 3
/** Tbilisi hours of the two daily pushes (UTC+4 all year — no DST in Georgia). */
export const TG_MORNING_HOUR = 8
export const TG_EVENING_HOUR = 21

// ── small words ─────────────────────────────────────────────────────────────

export const tgEscape = (v: string) => v.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
const r2 = (n: number) => Math.round(n * 100) / 100
const gel = (n: number) => `₾${r2(n).toFixed(2)}`
const signed = (n: number) => `${n > 0.005 ? '+' : n < -0.005 ? '−' : ''}₾${Math.abs(r2(n)).toFixed(2)}`
const TBILISI_MS = 4 * 3_600_000
const two = (n: number) => String(n).padStart(2, '0')
/** HH:MM in Tbilisi — built by hand: a Worker's Intl may not know ka-GE. */
export function tbilisiClock(iso: string | Date): string {
  const d = new Date(new Date(iso).getTime() + TBILISI_MS)
  return `${two(d.getUTCHours())}:${two(d.getUTCMinutes())}`
}
/** DD.MM HH:MM in Tbilisi. */
function tbilisiStamp(iso: string | Date): string {
  const d = new Date(new Date(iso).getTime() + TBILISI_MS)
  return `${two(d.getUTCDate())}.${two(d.getUTCMonth() + 1)} ${two(d.getUTCHours())}:${two(d.getUTCMinutes())}`
}
/** The Tbilisi hour and minute of an instant. */
export function tbilisiTime(now: Date): { hour: number; minute: number } {
  const d = new Date(now.getTime() + TBILISI_MS)
  return { hour: d.getUTCHours(), minute: d.getUTCMinutes() }
}
/** „ · +12.3%" against the earlier figure. Nothing when the earlier one was
 *  zero, or when the change is a thousand percent or more — against ₾6 of
 *  profit „+47161.5%" is noise, the two amounts beside it say it better. */
function change(now: number, before: number): string {
  if (Math.abs(before) < 0.005) return ''
  const pct = (now - before) / Math.abs(before) * 100
  if (Math.abs(pct) >= 1000) return ''
  return ` · ${pct >= 0 ? '+' : '−'}${Math.abs(pct).toFixed(1)}%`
}
/** A card. `expandable` shows its first lines and folds the rest. */
const card = (lines: string[], expandable = false) => `<blockquote${expandable ? ' expandable' : ''}>${lines.join('\n')}</blockquote>`

const TAKEOUT: Record<TgLang, Record<'owner' | 'bank' | 'other', string>> = {
  ka: { owner: 'მფლობელს', bank: 'ბანკში', other: 'სხვა' },
  ru: { owner: 'владельцу', bank: 'в банк', other: 'другое' },
}

type Plural = { one: string; few: string; many: string }
function ruPlural(n: number, p: Plural): string {
  const a = n % 10, b = n % 100
  return (a === 1 && b !== 11) ? p.one : (a >= 2 && a <= 4 && (b < 12 || b > 14)) ? p.few : p.many
}

const W = {
  ka: {
    today: 'დღეს', cash: 'ნაღდი', card: 'ბარათი', credit: 'ნისია', prepaid: 'წინასწარი თანხიდან', returns: 'დაბრუნება',
    exchange: 'გაცვლა', exchangesIn: 'მათ შორის გაცვლა',
    salesN: (n: number) => `${n} გაყიდვა`, receiptsN: (n: number) => `${n} ჩეკი`,
    noSales: 'დღეს ჯერ არაფერი გაყიდულა.', drawerNow: 'კასაში ახლა', noShift: 'ღია ცვლა არ არის.',
    shiftSince: (t: string) => `ცვლა ${t}-დან`, expectedNote: 'მოსალოდნელი ნაღდი — გაყიდვებით, შეტანით და გატანით დათვლილი.',
    shiftClosed: 'ცვლა დაიხურა', turnover: 'ბრუნვა', expected: 'მოსალოდნელი', counted: 'დათვლილი',
    ppTaken: 'წინასწარ მიღებული', ppBack: 'დაბრუნებული',
    exact: '🟢 სხვაობა არ არის', exactShort: '🟢 ზუსტია', short: '🔴 დანაკლისი', over: '🟡 ზედმეტობა',
    left: 'კასაში დარჩა', taken: 'გატანილი', salary: 'ხელფასი',
    evening: 'დღის მდგომარეობა', morning: 'გუშინ', profit: 'წმინდა მოგება', expenses: 'ხარჯი', prevDay: 'წინა დღეს',
    shifts: 'ცვლები', noShiftsClosed: 'გუშინ ცვლა არ დახურულა.', cashOut: 'სალაროდან გატანილი',
    stock: 'იწურება', stockOut: 'ამოიწურა', stockLow: 'ცოტაა', stockLeft: (q: number) => `დარჩა ${q}`, perDay: (x: number) => `დღეში ~${x}`,
    byBranch: 'ფილიალების მიხედვით', sellingLow: 'იყიდება, მაგრამ იწურება', outEverywhere: 'ყველგან ამოიწურა',
    canMove: (from: string, q: number, to: string) => `💡 გადატანა: ${from} (${q} ც.) → ${to}`,
    waybillNew: 'ახალი ზედნადები', tin: 'ს/კ', activated: (d: string) => `გააქტიურდა ${d}`,
    waybillWhere: 'მისაღებია: MartePOS → მომწოდებლები → RS.GE ზედნადებები',
    orderNew: 'ახალი ონლაინ შეკვეთა', orderFee: 'მიტანა', orderPickup: 'მაღაზიიდან წაიღებს',
    orderPay: { on_delivery: 'გადახდა მიტანისას', transfer: 'გადმორიცხვით', at_shop: 'გადახდა მაღაზიაში' } as Record<string, string>,
    orderWhere: 'დაურეკე და დაადასტურე: MartePOS → ონლაინ შეკვეთები',
    debts: 'ნისია — ვის რამდენი მართებს', total: 'სულ',
    alertShort: 'კასაში დააკლდა', alertStale: 'დაუხურავი ცვლა', days: 'დღე',
    alertLate: 'დახურვის შემდეგ შემოვიდა', alertOdd: (n: number) => `ოფლაინ, განსხვავებით: ${n} გაყიდვა`,
    alertEarlier: (n: number) => `ოფლაინ, წინა დღეებისთვის შემოვიდა: ${n} გაყიდვა`,
    sale: 'გაყიდვა', return: 'დაბრუნება', more: (n: number) => `და კიდევ ${n}…`,
    menuToday: '📊 დღეს', menuCash: '💵 კასა', menuYesterday: '🌅 გუშინ', menuStock: '📦 მარაგი', menuDebts: '📒 ვალები', menuWeek: '📅 კვირა', refresh: '🔄 განახლება',
    unchanged: 'ახალი არაფერია', refreshed: 'განახლდა', aiManager: 'AI მენეჯერი',
    askHelp: '🤖 <b>კითხვა</b> — ჰკითხე AI მენეჯერს, მაგ. „რა გავყიდე ამ კვირაში?", „რა არ გაყიდულა 30 დღეში?", „ვის რამდენი მართებს?"',
    askHint: 'დაწერე კითხვა, მაგ.: /ask რა გავყიდე ამ კვირაში?',
    welcome: (shop: string) => `✅ ეს ჩატი მიება მაღაზიას <b>${tgEscape(shop)}</b>.\n\n${card(['🌅 დილის ანგარიში — 08:00', '🧾 ცვლის დახურვა — როგორც კი დაიხურება', '🌙 დღის მდგომარეობა — 21:00', '🚨 დანაკარგების კონტროლი — როგორც კი მოხდება', '📅 კვირის ანგარიში — ორშაბათს 08:00', '📄 ახალი ზედნადები RS.GE-დან — როგორც კი მომწოდებელი გააქტიურებს'])}\nრომელი მოვიდეს, მფლობელი წყვეტს პარამეტრებში. ნებისმიერ დროს — ქვემოთ ღილაკებით.`,
    help: `ქვემოთ ღილაკები:\n${card(['📊 დღეს — გაყიდვა, ნაღდი, ბარათი, კასა', '💵 კასა — რამდენია კასაში ახლა', '🌅 გუშინ — გუშინდელი ანგარიში', '📦 მარაგი — რა იწურება', '📒 ვალები — ვის რამდენი მართებს და რა გვმართებს', '📅 კვირა — 7 დღე წინა კვირასთან'])}\n🔎 <b>ძებნა</b> — დაწერე პროდუქტის სახელი ან შტრიხკოდი: ფასი და მარაგი.\n📊 <b>პანელი</b> — სრული სურათი გრაფიკებით: ღილაკი შეტყობინების ველის გვერდით.\n/stop — ამ ჩატის გამოთიშვა`,
    notLinked: 'ეს ჩატი ჯერ არცერთ მაღაზიას არ ეკუთვნის.\n\nმიბმა: MartePOS → პარამეტრები → Telegram → „დაკავშირება" (მხოლოდ მაღაზიის მფლობელს შეუძლია).',
    privateOnly: 'უსაფრთხოებისთვის მაღაზია მხოლოდ პირად ჩატს ებმება. გახსენი ბოტთან პირადი ჩატი და კოდი იქ გააგზავნე — ეს კოდი არ გამოყენებულა.',
    badCode: 'ეს კოდი არ მუშაობს — ან ვადა გაუვიდა (10 წუთი), ან უკვე გამოყენებულია. პარამეტრებში ახალი აიღე.',
    otherShop: 'ეს ჩატი უკვე სხვა მაღაზიაზეა მიბმული. ჯერ /stop, მერე ახალი კოდი.',
    already: (shop: string) => `ეს ჩატი უკვე მიბმულია: <b>${tgEscape(shop)}</b>.`,
    full: 'ამ მაღაზიას უკვე 3 ჩატი აქვს მიბმული — ჯერ ერთი მოხსენი პარამეტრებში.',
    stopped: 'ჩატი გამოეთიშა მაღაზიას. აქ აღარაფერი მოვა.',
    quietMorning: 'გუშინ გაყიდვა არ ყოფილა.',
    error: 'ახლა ვერ დავთვალე — ცოტა ხანში სცადე.',
  },
  ru: {
    today: 'Сегодня', cash: 'Наличные', card: 'Карта', credit: 'В долг', prepaid: 'Из предоплаты', returns: 'Возвраты',
    exchange: 'Обмен', exchangesIn: 'В том числе обмен',
    salesN: (n: number) => `${n} ${ruPlural(n, { one: 'продажа', few: 'продажи', many: 'продаж' })}`,
    receiptsN: (n: number) => `${n} ${ruPlural(n, { one: 'чек', few: 'чека', many: 'чеков' })}`,
    noSales: 'Сегодня ещё ничего не продано.', drawerNow: 'В кассе сейчас', noShift: 'Открытых смен нет.',
    shiftSince: (t: string) => `смена с ${t}`, expectedNote: 'Ожидаемая наличность — по продажам, внесениям и изъятиям.',
    shiftClosed: 'Смена закрыта', turnover: 'Оборот', expected: 'Ожидалось', counted: 'Посчитано',
    ppTaken: 'Предоплата получена', ppBack: 'возвращено',
    exact: '🟢 Разницы нет', exactShort: '🟢 сходится', short: '🔴 Недостача', over: '🟡 Излишек',
    left: 'Оставлено в кассе', taken: 'Изъято', salary: 'Зарплата',
    evening: 'Итоги дня', morning: 'Вчера', profit: 'Чистая прибыль', expenses: 'Расходы', prevDay: 'накануне',
    shifts: 'Смены', noShiftsClosed: 'Вчера смены не закрывались.', cashOut: 'Изъято из кассы',
    stock: 'Заканчивается', stockOut: 'нет в наличии', stockLow: 'мало', stockLeft: (q: number) => `осталось ${q}`, perDay: (x: number) => `~${x} в день`,
    byBranch: 'По филиалам', sellingLow: 'Продаётся, но заканчивается', outEverywhere: 'нет нигде',
    canMove: (from: string, q: number, to: string) => `💡 перевезти: ${from} (${q} шт.) → ${to}`,
    waybillNew: 'Новая накладная', tin: 'код', activated: (d: string) => `активирована ${d}`,
    waybillWhere: 'Принять: MartePOS → Поставщики → Накладные RS.GE',
    orderNew: 'Новый онлайн-заказ', orderFee: 'доставка', orderPickup: 'самовывоз',
    orderPay: { on_delivery: 'оплата при получении', transfer: 'перевод', at_shop: 'оплата в магазине' } as Record<string, string>,
    orderWhere: 'Позвоните и подтвердите: MartePOS → Онлайн-заказы',
    debts: 'Нам должны', total: 'всего',
    alertShort: 'Недостача в кассе', alertStale: 'Незакрытая смена', days: 'дн.',
    alertLate: 'Пришло после закрытия', alertOdd: (n: number) => `Офлайн с расхождением: ${n} продаж`,
    alertEarlier: (n: number) => `Офлайн, за прошлые дни пришло: ${n} продаж`,
    sale: 'Продажа', return: 'Возврат', more: (n: number) => `и ещё ${n}…`,
    menuToday: '📊 Сегодня', menuCash: '💵 Касса', menuYesterday: '🌅 Вчера', menuStock: '📦 Остатки', menuDebts: '📒 Долги', menuWeek: '📅 Неделя', refresh: '🔄 Обновить',
    unchanged: 'Ничего нового', refreshed: 'Обновлено', aiManager: 'AI-менеджер',
    askHelp: '🤖 <b>Вопрос</b> — спроси AI-менеджера, например «Что я продал на этой неделе?», «Что не продавалось 30 дней?», «Кто сколько должен?»',
    askHint: 'Напиши вопрос, например: /ask что я продал на этой неделе?',
    welcome: (shop: string) => `✅ Этот чат привязан к магазину <b>${tgEscape(shop)}</b>.\n\n${card(['🌅 Утренний отчёт — 08:00', '🧾 Закрытие смены — сразу после закрытия', '🌙 Итоги дня — 21:00', '🚨 Контроль потерь — сразу, как случилось', '📅 Итоги недели — в понедельник 08:00', '📄 Новая накладная из RS.GE — как только поставщик её активирует'])}\nЧто именно приходит, решает владелец в настройках. В любой момент — кнопки внизу.`,
    help: `Кнопки внизу:\n${card(['📊 Сегодня — продажи, наличные, карта, касса', '💵 Касса — сколько в кассе сейчас', '🌅 Вчера — отчёт за вчера', '📦 Остатки — что заканчивается', '📒 Долги — кто нам должен и кому должны мы', '📅 Неделя — 7 дней против прошлой недели'])}\n🔎 <b>Поиск</b> — напишите название товара или штрихкод: цена и остаток.\n📊 <b>Панель</b> — полная картина с графиками: кнопка рядом с полем сообщения.\n/stop — отключить этот чат`,
    notLinked: 'Этот чат ещё не привязан ни к одному магазину.\n\nПривязка: MartePOS → Настройки → Telegram → «Подключить» (только владелец магазина).',
    privateOnly: 'Для безопасности магазин можно привязать только в личном чате. Откройте личный чат с ботом и отправьте код там — этот код не использован.',
    badCode: 'Этот код не работает — истёк (10 минут) или уже использован. Возьмите новый в настройках.',
    otherShop: 'Этот чат уже привязан к другому магазину. Сначала /stop, затем новый код.',
    already: (shop: string) => `Этот чат уже привязан: <b>${tgEscape(shop)}</b>.`,
    full: 'У магазина уже 3 чата — сначала удалите один в настройках.',
    stopped: 'Чат отключён от магазина. Сюда больше ничего не придёт.',
    quietMorning: 'Вчера продаж не было.',
    error: 'Не удалось посчитать — попробуйте чуть позже.',
  },
} as const

export const tgWords = (lang: TgLang) => W[lang]

/** /help, with the question line where the AI manager is switched on. */
export const tgHelp = (lang: TgLang, ai: boolean) => (ai ? `${W[lang].help}\n${W[lang].askHelp}` : W[lang].help)

/** 🤖 the AI manager's answer to a question — our label, its words, escaped; a long one folds. */
export function askText(answer: string, lang: TgLang): string {
  const lines = answer.split('\n')
  return `🤖 <b>${W[lang].aiManager}</b>\n${card(lines.map(tgEscape), lines.length > 8 || answer.length > 600)}`
}
export const tgLangOf = (code: string | null | undefined): TgLang => (code?.toLowerCase().startsWith('ru') ? 'ru' : 'ka')

/** A shop's link code may be claimed only by the owner's one-to-one chat. */
export const canLinkTelegramChat = (chatType: string): boolean => chatType === 'private'

// ── the buttons ─────────────────────────────────────────────────────────────

/** The menu under the chat's input — always there, so nobody types a command. */
export function tgMenu(lang: TgLang) {
  const w = W[lang]
  return {
    keyboard: [[{ text: w.menuToday }, { text: w.menuCash }], [{ text: w.menuYesterday }, { text: w.menuStock }], [{ text: w.menuDebts }, { text: w.menuWeek }]],
    resize_keyboard: true,
    is_persistent: true,
  }
}

/** „🔄" under a live message: pressing it rewrites the message with this minute's numbers. */
export type TgLive = 'status' | 'cash'
export function tgRefresh(lang: TgLang, what: TgLive) {
  return { inline_keyboard: [[{ text: W[lang].refresh, callback_data: what }]] }
}

/** A menu button's text (either language) is its command; anything else is null. */
export function menuCommand(text: string | null | undefined): string | null {
  const t = (text ?? '').trim()
  for (const w of [W.ka, W.ru]) {
    if (t === w.menuToday) return '/status'
    if (t === w.menuCash) return '/cash'
    if (t === w.menuYesterday) return '/yesterday'
    if (t === w.menuStock) return '/stock'
    if (t === w.menuDebts) return '/debts'
    if (t === w.menuWeek) return '/week'
  }
  return null
}

// ── today, live ─────────────────────────────────────────────────────────────

export type LiveInput = {
  /** Today's sales (Tbilisi day) of the whole shop. */
  todaySales: Sale[]
  /** Shifts open right now. */
  openShifts: Shift[]
  /** Every row of those open shifts — their sales may start before today. */
  shiftSales: Sale[]
  customerPayments: CustomerPayment[]
  supplierPayments: SupplierPayment[]
  cashMovements: CashMovement[]
  /** 1.57 — the open shifts' gift card / deposit moves (their cash is in the drawer) */
  prepaidMoves?: PrepaidMove[]
  branchNames: Map<string, string>
}

export type Drawer = { branch: string | null; cashier: string; openedAt: string; expected: number; card: number; count: number }

export type Live = {
  count: number
  revenue: number
  cash: number
  card: number
  credit: number
  /** 1.57 — paid from a gift card or a deposit */
  prepaid: number
  returnsCount: number
  returnsAmount: number
  drawers: Drawer[]
}

/**
 * Today so far, and every drawer as it stands. A drawer's cash is
 * `expectedCash(opening, cashTerms(this shift's rows))` — what the close
 * dialog would propose if the shift closed this minute; its card is the card
 * RAIL of the shift's sales, as `close_shift()` counts it.
 */
export function liveNumbers(input: LiveInput): Live {
  const sold = input.todaySales.filter((s) => s.type === 'sale')
  const returns = input.todaySales.filter((s) => s.type === 'return')
  const rails = sumRails(input.todaySales)
  const drawers = [...input.openShifts]
    .sort((a, b) => a.opened_at.localeCompare(b.opened_at))
    .map((sh): Drawer => {
      const ss = input.shiftSales.filter((s) => s.shift_id === sh.id)
      const t = cashTerms({
        sales: ss,
        customerPayments: input.customerPayments.filter((p) => p.shift_id === sh.id),
        supplierPayments: input.supplierPayments.filter((p) => p.shift_id === sh.id),
        cashMovements: input.cashMovements.filter((m) => m.shift_id === sh.id),
        prepaidMoves: (input.prepaidMoves ?? []).filter((m) => m.shift_id === sh.id),
      })
      return {
        branch: sh.branch_id ? input.branchNames.get(sh.branch_id) ?? null : null,
        cashier: sh.cashier_name || '—',
        openedAt: sh.opened_at,
        expected: r2(expectedCash(sh.opening_cash, t)),
        card: r2(ss.filter((s) => s.type === 'sale').reduce((a, s) => a + saleRails(s).card, 0)),
        count: ss.filter((s) => s.type === 'sale').length,
      }
    })
  return {
    count: sold.length,
    revenue: r2(input.todaySales.reduce((a, s) => a + saleAmount(s), 0)),
    cash: r2(rails.cash),
    card: r2(rails.card),
    credit: r2(rails.credit),
    prepaid: r2(rails.prepaid),
    returnsCount: returns.length,
    returnsAmount: r2(returns.reduce((a, s) => a + s.total, 0)),
    drawers,
  }
}

/** One card per open drawer: the cash in bold, who and since when under it. */
function drawerCards(live: Live, lang: TgLang): string {
  const w = W[lang]
  if (!live.drawers.length) return card([w.noShift])
  return live.drawers.map((d) => card([
    `<b>${gel(d.expected)}</b> · ${d.branch ? `${tgEscape(d.branch)} — ` : ''}${tgEscape(d.cashier)}`,
    `${w.shiftSince(tbilisiClock(d.openedAt))} · 💳 ${gel(d.card)}`,
  ])).join('\n')
}

/** The day's money: the total in bold, then each rail on its own line. */
function moneyLines(m: { count: number; revenue: number; cash: number; card: number; credit: number; prepaid?: number; returnsCount: number; returnsAmount: number }, lang: TgLang): string[] {
  const w = W[lang]
  const lines = [`<b>${gel(m.revenue)}</b> — ${w.salesN(m.count)}`, `💵 ${w.cash} ${gel(m.cash)}`, `💳 ${w.card} ${gel(m.card)}`]
  if (Math.abs(m.credit) > 0.005) lines.push(`📒 ${w.credit} ${gel(m.credit)}`)
  if (Math.abs(m.prepaid ?? 0) > 0.005) lines.push(`🎁 ${w.prepaid} ${gel(m.prepaid ?? 0)}`)
  if (m.returnsCount) lines.push(`↩️ ${w.returns} (${m.returnsCount}) −${gel(m.returnsAmount)}`)
  return lines
}

/** 📊 today, the evening push and the owner's test message. */
export function statusText(shop: string, live: Live, now: Date, lang: TgLang, title?: string): string {
  const w = W[lang]
  return [
    title ?? `📊 <b>${tgEscape(shop)}</b>`,
    `${w.today}, ${tbilisiClock(now)}`,
    '',
    live.count === 0 && live.returnsCount === 0 ? card([w.noSales]) : card(moneyLines(live, lang)),
    '',
    `🏦 <b>${w.drawerNow}</b>`,
    drawerCards(live, lang),
  ].join('\n')
}

export const eveningText = (shop: string, live: Live, now: Date, lang: TgLang) =>
  statusText(shop, live, now, lang, `🌙 <b>${tgEscape(shop)}</b> — ${W[lang].evening}`)

/** 💵 — only the drawers. */
export function cashText(shop: string, live: Live, now: Date, lang: TgLang): string {
  const w = W[lang]
  return [`🏦 <b>${w.drawerNow}</b> · ${tgEscape(shop)}`, tbilisiClock(now), '', drawerCards(live, lang), '', `<i>${w.expectedNote}</i>`].join('\n')
}

// ── a shift closed ──────────────────────────────────────────────────────────

/** The difference, coloured the only way Telegram allows. */
function diffLine(diff: number, lang: TgLang): string {
  const w = W[lang]
  if (Math.abs(diff) < 0.005) return `<b>${w.exact}</b>`
  return `<b>${diff < 0 ? w.short : w.over} ${signed(diff)}</b>`
}

/** From the shift row itself — every number frozen by `close_shift()`, as the Z-report prints them. */
export function shiftClosedText(shop: string, sh: Shift, branch: string | null, lang: TgLang, exchanges?: ExchangeShiftTotals): string {
  const w = W[lang]
  // 176 — the sales a gift card or a deposit paid are the third rail of the turnover
  const ppSales = sh.prepaid_sales ?? 0
  const turnover = sh.cash_sales + sh.card_sales + sh.credit_sales + ppSales - sh.returns_total
  const money = [`<b>${gel(turnover)}</b> ${w.turnover} — ${w.receiptsN(sh.sales_count)}`, `💵 ${w.cash} ${gel(sh.cash_sales)}`, `💳 ${w.card} ${gel(sh.card_sales)}`]
  if (ppSales > 0.005) money.push(`🎁 ${w.prepaid} ${gel(ppSales)}`)
  if (sh.credit_sales > 0.005) money.push(`📒 ${w.credit} ${gel(sh.credit_sales)}`)
  if (sh.returns_total > 0.005) money.push(`↩️ ${w.returns} −${gel(sh.returns_total)}`)
  // 1.36 — the exchanges inside those returns, counted from the shift's sale rows
  if (exchanges && exchanges.count > 0) money.push(`🔁 ${w.exchangesIn}: ${exchanges.count} · +${gel(exchanges.extra)}`)
  const out = [
    `🧾 <b>${w.shiftClosed}</b> — ${tgEscape(sh.cashier_name || '—')}`,
    `${tgEscape(shop)}${branch ? ` · ${tgEscape(branch)}` : ''} · ${tbilisiStamp(sh.opened_at)} → ${sh.closed_at ? tbilisiClock(sh.closed_at) : '—'}`,
    '',
    card(money),
    card([`${w.expected} ${gel(sh.expected_cash)}`, `${w.counted} ${gel(sh.closing_cash ?? 0)}`, diffLine(sh.difference, lang)]),
  ]
  // 176 — gift cards sold / deposits taken (cash + card) and handed back: not takings,
  // but the cash part is inside the expected figure above, so the owner sees why
  const ppIn = (sh.prepaid_in_cash ?? 0) + (sh.prepaid_in_card ?? 0)
  const ppOut = (sh.prepaid_out_cash ?? 0) + (sh.prepaid_out_card ?? 0)
  if (ppIn > 0.005 || ppOut > 0.005) {
    out.push('', `🎁 ${w.ppTaken} +${gel(ppIn)}${ppOut > 0.005 ? ` · ${w.ppBack} −${gel(ppOut)}` : ''}`)
  }
  const after: string[] = []
  // 137 — the cashier's salary left the count before the float and the take-out
  if ((sh.closing_salary ?? 0) > 0.005) after.push(`💰 ${w.salary} ${gel(sh.closing_salary ?? 0)}`)
  if (sh.closing_float != null) after.push(`🏦 ${w.left} <b>${gel(sh.closing_float)}</b>`)
  const taken = takenAtClose(sh)
  if (taken > 0.005) {
    const reason = sh.takeout_reason ?? 'other'
    const where = reason === 'bank' && sh.takeout_bank ? `${TAKEOUT[lang].bank} (${CARD_PROVIDER_LABELS[sh.takeout_bank]})` : TAKEOUT[lang][reason]
    after.push(`📤 ${w.taken} ${gel(taken)} — ${where}${sh.takeout_note ? ` · ${tgEscape(sh.takeout_note)}` : ''}`)
  }
  if (after.length) out.push('', ...after)
  return out.join('\n')
}

// ── one sale (off unless the owner turns it on) ─────────────────────────────

export type SaleLine = { name: string; quantity: number }

export function saleText(sale: Sale, lines: SaleLine[], lang: TgLang): string {
  const w = W[lang]
  const r = saleRails(sale)
  // 1.58 (1.57's Codex Minor #1): every rail that carried money — a prepaid share is the prepaid, not cash
  const parts = [
    r.cash !== 0 ? `💵 ${gel(Math.abs(r.cash))}` : null,
    r.card !== 0 ? `💳 ${gel(Math.abs(r.card))}` : null,
    r.prepaid !== 0 ? `🎁 ${gel(Math.abs(r.prepaid))}` : null,
  ].filter((x): x is string => x !== null)
  const how = sale.payment_method === 'credit' ? `📒 ${w.credit}`
    : parts.length > 1 ? parts.join(' + ')
    : r.prepaid !== 0 ? `🎁 ${w.prepaid}`
    : r.card !== 0 ? `💳 ${w.card}` : `💵 ${w.cash}`
  const head = sale.type === 'return'
    ? `↩️ <b>${w.return} ${gel(sale.total)}</b> · ${how}`
    : `🛒 <b>${gel(sale.total)}</b> · ${how}`
  const shown = lines.slice(0, 5).map((l) => `${tgEscape(l.name)} × ${Number.isInteger(l.quantity) ? l.quantity : l.quantity.toFixed(2)}`)
  if (lines.length > 5) shown.push(w.more(lines.length - 5))
  return [`${head}${sale.cashier_name ? ` · ${tgEscape(sale.cashier_name)}` : ''}`, shown.length ? card(shown) : ''].filter(Boolean).join('\n')
}

/** 1.36 — an exchange is ONE message: what came back, what went, what was paid on top. */
export function exchangeText(sale: Sale, ret: Sale, back: SaleLine[], given: SaleLine[], lang: TgLang): string {
  const w = W[lang]
  const extra = Math.round((sale.total - ret.total) * 100) / 100
  const names = (ls: SaleLine[]) => ls.slice(0, 3).map((l) => `${tgEscape(l.name)} × ${Number.isInteger(l.quantity) ? l.quantity : l.quantity.toFixed(2)}`).join(', ')
  const lines = [back.length ? `↩️ ${names(back)}` : '', given.length ? `➡️ ${names(given)}` : ''].filter(Boolean)
  return [`🔁 <b>${w.exchange}</b> · +${gel(extra)}${sale.cashier_name ? ` · ${tgEscape(sale.cashier_name)}` : ''}`, lines.length ? card(lines) : ''].filter(Boolean).join('\n')
}

/**
 * 1.36 — the per-sale feed: an exchange is told once, on its SALE leg, so its
 * return leg never enters the batch. Dropped BEFORE the batch is cut (Codex,
 * build review #4): never marked as sent, it would fill the 20-row batch for
 * an hour and starve every later sale.
 */
export function saleFeedRows<T extends { type?: unknown; exchange_id?: unknown }>(rows: T[]): T[] {
  return rows.filter((r) => !(r.exchange_id && r.type === 'return'))
}

// ── the morning report, as the mail tells it ────────────────────────────────

function stockLines(r: OwnerReport, lang: TgLang): string[] {
  const w = W[lang]
  return r.stock.urgent.map((u) => `${tgEscape(u.name)} — ${u.quantity === 0 ? `<b>${w.stockOut}</b>` : w.stockLeft(u.quantity)} · ${w.perDay(u.perDay)}`)
}

export function morningText(r: OwnerReport, lang: TgLang, costs?: CostsToday, note?: string | null, branches?: BranchStock | null): string {
  const w = W[lang]
  const out: string[] = [`🌅 <b>${tgEscape(r.shopName)}</b> — ${w.morning}`, lang === 'ka' ? tgEscape(r.dayLabel) : `${r.day.slice(8, 10)}.${r.day.slice(5, 7)}.${r.day.slice(0, 4)}`, '']
  // The AI manager's note (lib/ai/brief.ts) — first, so the phone's notification shows it; every number in it was checked against the report.
  if (note) out.push(card([`🤖 <b>${w.aiManager}</b>`, tgEscape(note)]))

  const alerts: string[] = []
  const short = r.shifts.filter((s) => s.difference < -0.005)
  if (short.length) alerts.push(`⚠️ ${w.alertShort}: ${short.map((s) => `${tgEscape(s.cashier)} ${signed(s.difference)}`).join(', ')}`)
  if (r.staleShifts.length) alerts.push(`⚠️ ${w.alertStale}: ${r.staleShifts.map((s) => `${tgEscape(s.cashier)} (${s.days} ${w.days})`).join(', ')}`)
  // 140 — what reached a closed shift, and offline sales that came odd
  if (r.lateArrivals.length) alerts.push(`⚠️ ${w.alertLate}: ${r.lateArrivals.map((l) => `${tgEscape(l.cashier)} ${signed(l.amount)}`).join(', ')}`)
  if (r.offlineOdd) alerts.push(`⚠️ ${w.alertOdd(r.offlineOdd)}`)
  if (r.offlineEarlier.count) alerts.push(`⚠️ ${w.alertEarlier(r.offlineEarlier.count)} ${signed(r.offlineEarlier.amount)}`)
  if (alerts.length) out.push(card(alerts))

  if (r.sales.count === 0 && r.sales.returnsCount === 0) out.push(card([w.quietMorning]))
  else {
    const money = moneyLines(r.sales, lang)
    money.push(`🧾 ${w.expenses} ${gel(r.profit.expenses)}`)
    money.push(`📈 ${w.profit} <b>${gel(r.profit.net)}</b>`)
    if (r.profit.prevNet != null) {
      money.push(`<i>${w.prevDay} ${gel(r.profit.prevNet)}${change(r.profit.net, r.profit.prevNet)}</i>`)
    }
    out.push(card(money))
  }

  out.push('', `👥 <b>${w.shifts}</b>`)
  out.push(card(r.shifts.length
    ? r.shifts.map((s) => `${tgEscape(s.cashier)} — ${Math.abs(s.difference) < 0.005 ? w.exactShort : `${s.difference < 0 ? '🔴' : '🟡'} ${signed(s.difference)}`}`)
    : [w.noShiftsClosed]))

  if (r.cashOut.items.length) {
    out.push('', `📤 <b>${w.cashOut}</b> · ${gel(r.cashOut.total)}`)
    out.push(card(r.cashOut.items.map((m) => `${tgEscape(m.label)} — ${gel(m.amount)}${m.note ? ` · ${tgEscape(m.note)}` : ''}`), r.cashOut.items.length > 3))
  }
  if (costs && (costs.today.length || costs.waiting.length)) {
    const d = D[lang]
    out.push('', `📅 <b>${d.costsToday}</b>`)
    out.push(card([
      ...costs.today.map((c) => `${costName(c, lang)} — ${gel(c.amount)}`),
      ...costs.waiting.map((c) => `${costName(c, lang)} — ⏳ ${d.waitingWhere}`),
    ]))
  }
  if (branches) {
    // 132 — a shop with branches reads its shelves branch by branch.
    if (branches.urgent.length) {
      out.push('', `📦 <b>${w.stock}</b> · ${w.byBranch}`)
      out.push(...branchStockBody(branches, lang))
    }
  } else if (r.stock.urgent.length) {
    out.push('', `📦 <b>${w.stock}</b> · ${w.stockOut} ${r.stock.outCount} · ${w.stockLow} ${r.stock.lowCount}`)
    out.push(card(stockLines(r, lang), r.stock.urgent.length > 3))
  }
  if (r.debts.total > 0.005) {
    out.push('', `📒 <b>${w.debts}</b> · ${w.total} ${gel(r.debts.total)}`)
    out.push(card(r.debts.top.map((d) => `${tgEscape(d.name)} — ${gel(d.balance)}`)))
  }
  return out.join('\n')
}

/** 📦 — the morning report's shelf part, alone; branch by branch where the shop has branches (132). */
export function stockText(r: OwnerReport, lang: TgLang, branches?: BranchStock | null): string {
  const w = W[lang]
  if (branches) return [`📦 <b>${w.stock}</b> · ${tgEscape(r.shopName)}`, '', ...branchStockBody(branches, lang)].join('\n')
  const head = [`📦 <b>${w.stock}</b> · ${tgEscape(r.shopName)}`, `${w.stockOut} ${r.stock.outCount} · ${w.stockLow} ${r.stock.lowCount}`, '']
  return [...head, card(r.stock.urgent.length ? stockLines(r, lang) : [`${w.stockOut} ${r.stock.outCount} · ${w.stockLow} ${r.stock.lowCount}`])].join('\n')
}

// ── 📦 branch by branch (132) ───────────────────────────────────────────────

/** What `branch_stock_alerts` returns, typed: every active branch with its
 *  out/low counts, and the products that SELL in a branch and are out or low
 *  THERE — each with its quantity in every branch that carries it. */
export type BranchStock = {
  branches: { id: string; name: string; out: number; low: number }[]
  urgent: { productId: string; name: string; sold: number; stock: Record<string, number> }[]
}

/** The RPC's JSON → BranchStock; null for a shop with one branch (nothing to split). */
export function branchStockOf(raw: unknown): BranchStock | null {
  const o = (raw ?? {}) as { branches?: unknown[]; urgent?: unknown[] }
  const branches = (o.branches ?? []).map((b) => {
    const r = b as Record<string, unknown>
    return { id: String(r.id), name: String(r.name ?? '—'), out: Number(r.out ?? 0), low: Number(r.low ?? 0) }
  })
  if (branches.length < 2) return null
  const urgent = (o.urgent ?? []).map((u) => {
    const r = u as Record<string, unknown>
    const stock: Record<string, number> = {}
    for (const [k, v] of Object.entries((r.stock ?? {}) as Record<string, unknown>)) stock[k] = Number(v)
    return { productId: String(r.product_id), name: String(r.name ?? '—'), sold: Number(r.sold ?? 0), stock }
  })
  return { branches, urgent }
}

/**
 * The 📦 body for a shop with branches: each branch's out/low counts, then
 * the sellers running out somewhere — where they are out, what the others
 * hold, and, when one branch is out while another has more than the low
 * mark, which way a transfer would go. No grammar on branch names (Georgian
 * would need a case ending on each): „ბათუმი (12 ც.) → მთავარი".
 */
export function branchStockBody(b: BranchStock, lang: TgLang): string[] {
  const w = W[lang]
  const out = [card(b.branches.map((br) => `${tgEscape(br.name)} — ${w.stockOut} ${br.out} · ${w.stockLow} ${br.low}`))]
  if (!b.urgent.length) return out
  const lines: string[] = []
  for (const u of b.urgent) {
    const held = b.branches.filter((br) => br.id in u.stock)
    const parts = held.map((br) => {
      const q = u.stock[br.id]
      return q <= 0 ? `${tgEscape(br.name)} <b>${w.stockOut}</b>` : `${tgEscape(br.name)} ${qtyText(q)}`
    })
    lines.push(`<b>${tgEscape(u.name)}</b> · ${w.perDay(Math.round((u.sold / 30) * 10) / 10)}`)
    if (held.length && held.every((br) => u.stock[br.id] <= 0)) lines.push(`${w.outEverywhere}`)
    else lines.push(parts.join(' · '))
    const empty = held.filter((br) => u.stock[br.id] <= 0)
    const donor = [...held].sort((x, y) => u.stock[y.id] - u.stock[x.id])[0]
    if (empty.length && donor && u.stock[donor.id] > LOW_STOCK) {
      lines.push(w.canMove(tgEscape(donor.name), Number(qtyText(u.stock[donor.id])), empty.map((br) => tgEscape(br.name)).join(', ')))
    }
    lines.push('')
  }
  while (lines.length && lines[lines.length - 1] === '') lines.pop()
  out.push('', `<b>${w.sellingLow}</b>`, card(lines, b.urgent.length > 2))
  return out
}

// ── 📄 a new RS.GE waybill ──────────────────────────────────────────────────

export type NewWaybill = { sellerName: string | null; sellerTin: string | null; amount: number; number: string | null; activateDate: string | null }

/** RS.GE writes Tbilisi time with no zone („2026-08-31T09:51:00") — read as written, never shifted. */
export function rsDayTime(v: string | null): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(v ?? '')
  return m ? `${m[3]}.${m[2]} ${m[4]}:${m[5]}` : null
}

export function waybillText(shop: string, wb: NewWaybill, lang: TgLang): string {
  const w = W[lang]
  const who = [wb.sellerName ? `<b>${tgEscape(wb.sellerName)}</b>` : null, wb.sellerTin ? `${w.tin} ${tgEscape(wb.sellerTin)}` : null].filter(Boolean).join(' · ')
  const when = rsDayTime(wb.activateDate)
  return [
    `📄 <b>${w.waybillNew}</b> · ${tgEscape(shop)}`,
    card([who || '—', `<b>${gel(wb.amount)}</b>${wb.number ? ` · № ${tgEscape(wb.number)}` : ''}`, ...(when ? [w.activated(when)] : [])]),
    w.waybillWhere,
  ].join('\n')
}

// ── 🛍 a new online order (B2) ──────────────────────────────────────────────

export type NewOnlineOrder = {
  number: number
  customerName: string | null
  customerPhone: string | null
  delivery: 'courier' | 'pickup'
  city: string | null
  payment: string
  deliveryFee: number
  total: number
  lines: { name: string; quantity: number }[]
}

/** The shop must call the buyer and confirm — so the message carries who and what. Owner's chats only (123). */
export function orderText(shop: string, o: NewOnlineOrder, lang: TgLang): string {
  const w = W[lang]
  const who = [o.customerName ? `<b>${tgEscape(o.customerName)}</b>` : null, o.customerPhone ? tgEscape(o.customerPhone) : null].filter(Boolean).join(' · ')
  const where = o.delivery === 'pickup' ? w.orderPickup : `${w.orderFee}${o.city ? ` · ${tgEscape(o.city)}` : ''}${o.deliveryFee > 0 ? ` · ${gel(o.deliveryFee)}` : ''}`
  return [
    `🛍 <b>${w.orderNew} #${o.number}</b> · ${tgEscape(shop)}`,
    card([
      who || '—',
      ...o.lines.slice(0, 10).map((l) => `${tgEscape(l.name)}${l.quantity > 1 ? ` × ${l.quantity}` : ''}`),
      ...(o.lines.length > 10 ? [`… +${o.lines.length - 10}`] : []),
      `${where} · ${w.orderPay[o.payment] ?? tgEscape(o.payment)}`,
      `<b>${gel(o.total)}</b>`,
    ]),
    w.orderWhere,
  ].join('\n')
}

// ── 📒 who owes, whom we owe, what is due ───────────────────────────────────

type Owing = { id: string; name: string; phone: string | null; balance: number }

export type DebtsInput = {
  shop: string
  /** Settings → the IBAN line the ნისია page's reminder carries. */
  bankDetails: string
  /** Tbilisi, YYYY-MM-DD. */
  today: string
  /** Customers with a balance — positive = they owe the shop. */
  customers: Owing[]
  /** Suppliers with a balance — positive = the shop owes THEM (lib/mock-data). */
  suppliers: Owing[]
  recurring: RecurringExpense[]
}

/** The morning's monthly costs: what books today (already booked or about to be) and what waits for its amount. */
export type CostsToday = {
  today: { category: ExpenseCategory; note: string | null; amount: number }[]
  waiting: { category: ExpenseCategory; note: string | null }[]
}

const D = {
  ka: {
    title: 'ვალები', nobody: 'ნისიაზე არავის არაფერი მართებს.',
    weOwe: 'მომწოდებლები — ჩვენი ვალი', weOweNone: 'მომწოდებლებთან ვალი არ გვაქვს.',
    costs: 'ყოველთვიური ხარჯები', waiting: 'ელოდება თანხას', soon: 'მომდევნო 7 დღეში:',
    costsToday: 'დღეს — ყოველთვიური ხარჯი', waitingWhere: 'ელოდება თანხას (ბუღალტერია)',
    remindHint: '💬 ქვემოთ — WhatsApp შეხსენება მოვალეს: ტექსტი უკვე დაწერილია, გაგზავნამდე ნახავ.',
  },
  ru: {
    title: 'Долги', nobody: 'Никто не должен магазину.',
    weOwe: 'Мы должны поставщикам', weOweNone: 'Поставщикам не должны.',
    costs: 'Ежемесячные расходы', waiting: 'ждёт суммы', soon: 'в ближайшие 7 дней:',
    costsToday: 'Сегодня — ежемесячные расходы', waitingWhere: 'ждёт суммы (Бухгалтерия)',
    remindHint: '💬 Ниже — напоминание должнику в WhatsApp: текст уже написан (по-грузински), перед отправкой вы его увидите.',
  },
} as const

const ddmm = (day: string) => `${day.slice(8, 10)}.${day.slice(5, 7)}`
const LIST = 15

/** „ქირა · ოფისი" — the category in the chat's language (the app's own words), the owner's note after it. Plain text. */
export function costLabel(c: { category: string; note: string | null }, lang: TgLang): string {
  const key = `expenseCat.${c.category}` as keyof typeof kaCommon
  const cat = String((lang === 'ru' ? ruCommon : kaCommon)[key] ?? c.category)
  return `${cat}${c.note ? ` · ${c.note}` : ''}`
}
const costName = (c: { category: ExpenseCategory; note: string | null }, lang: TgLang) => tgEscape(costLabel(c, lang))

/** A long list stops at 15 and says how many more. */
function capped(lines: string[], lang: TgLang): string[] {
  return lines.length > LIST ? [...lines.slice(0, LIST), W[lang].more(lines.length - LIST)] : lines
}

const owing = (list: Owing[]) => list.filter((x) => x.balance > 0.005).sort((a, b) => b.balance - a.balance)

export function debtsText(input: DebtsInput, lang: TgLang): string {
  const w = W[lang], d = D[lang]
  const theyOwe = owing(input.customers)
  const weOwe = owing(input.suppliers)
  const out = [`📒 <b>${d.title}</b> · ${tgEscape(input.shop)}`, '']

  out.push(`👥 <b>${w.debts}</b>${theyOwe.length ? ` · ${w.total} ${gel(outstandingDebt(theyOwe as Parameters<typeof outstandingDebt>[0]))}` : ''}`)
  out.push(theyOwe.length
    ? card(capped(theyOwe.map((c) => `${tgEscape(c.name)} — <b>${gel(c.balance)}</b>`), lang), theyOwe.length > 5)
    : card([d.nobody]))

  out.push('', `🚚 <b>${d.weOwe}</b>${weOwe.length ? ` · ${w.total} ${gel(weOwe.reduce((a, s) => a + s.balance, 0))}` : ''}`)
  out.push(weOwe.length
    ? card(capped(weOwe.map((s) => `${tgEscape(s.name)} — <b>${gel(s.balance)}</b>`), lang), weOwe.length > 5)
    : card([d.weOweNone]))

  const waiting = waitingForAmount(input.recurring, input.today)
  const soon = comingUp(input.recurring, input.today, 7)
  if (waiting.length || soon.length) {
    out.push('', `📅 <b>${d.costs}</b>`)
    out.push(card([
      ...waiting.map((r) => `⏳ ${costName(r, lang)} — ${d.waiting} · ${ddmm(r.next_due)}`),
      ...(soon.length ? [`<i>${d.soon}</i>`] : []),
      // An ASK cost's amount is last month's — shown as an estimate.
      ...soon.map((r) => `${costName(r, lang)} — ${r.mode === 'ask' ? '~' : ''}${gel(r.amount)} · ${ddmm(r.next_due)}`),
    ]))
  }
  if (debtButtons(input).inline_keyboard.length) out.push('', d.remindHint)
  return out.join('\n')
}

/** One WhatsApp button per debtor with a phone (the biggest first, at most 8) —
 *  the ნისია page's reminder, typed and waiting; Georgian, because the customer reads it. */
export function debtButtons(input: DebtsInput) {
  const rows = owing(input.customers)
    .map((c) => ({ c, phone: waPhone(c.phone) }))
    .filter((x): x is { c: Owing; phone: string } => x.phone != null)
    .slice(0, 8)
    .map(({ c, phone }) => [{
      text: `💬 ${c.name.length > 28 ? `${c.name.slice(0, 27)}…` : c.name} · ${gel(c.balance)}`,
      url: whatsAppLink(phone, reminderText(c, input.shop, input.bankDetails)),
    }])
  return { inline_keyboard: rows }
}

// ── 📅 the week ─────────────────────────────────────────────────────────────

const K = {
  ka: {
    title: 'კვირა', quiet: 'ამ 7 დღეში გაყიდვა არ ყოფილა.', prev: 'წინა კვირას', byDay: 'დღეების მიხედვით',
    top: 'ყველაზე მეტი შემოსავალი', days: ['კვი', 'ორშ', 'სამ', 'ოთხ', 'ხუთ', 'პარ', 'შაბ'],
  },
  ru: {
    title: 'Неделя', quiet: 'За эти 7 дней продаж не было.', prev: 'неделей ранее', byDay: 'По дням',
    top: 'Больше всего выручки', days: ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'],
  },
} as const


/** Eight cells, filled in proportion to the week's best day — monospace, so the bars line up. */
function bar(value: number, max: number): string {
  const n = max > 0.005 ? Math.round(Math.max(0, value) / max * 8) : 0
  return '█'.repeat(n) + '░'.repeat(8 - n)
}

export function weekText(shop: string, r: WeekReport, lang: TgLang): string {
  const w = W[lang], k = K[lang]
  const out = [`📅 <b>${tgEscape(shop)}</b> — ${k.title}`, `${ddmm(r.from)} – ${ddmm(r.to)}`, '']
  if (r.count === 0 && r.returnsCount === 0) {
    out.push(card([k.quiet, ...(r.expenses > 0.005 ? [`🧾 ${w.expenses} ${gel(r.expenses)}`] : [])]))
    return out.join('\n')
  }
  const money = moneyLines(r, lang)
  if (r.prev) money.push(`<i>${k.prev} ${gel(r.prev.revenue)}${change(r.revenue, r.prev.revenue)}</i>`)
  money.push(`🧾 ${w.expenses} ${gel(r.expenses)}`)
  money.push(`📈 ${w.profit} <b>${gel(r.net)}</b>`)
  if (r.prev) money.push(`<i>${k.prev} ${gel(r.prev.net)}${change(r.net, r.prev.net)}</i>`)
  out.push(card(money))

  const max = Math.max(0, ...r.days.map((d) => d.revenue))
  out.push('', `📊 <b>${k.byDay}</b>`)
  out.push(card(r.days.map((d) =>
    `<code>${bar(d.revenue, max)}</code> ${k.days[new Date(`${d.day}T12:00:00Z`).getUTCDay()]} ${ddmm(d.day)} — ${gel(d.revenue)}${r.best?.day === d.day ? ' 🏆' : ''}`)))

  if (r.top.length) {
    out.push('', `🔝 <b>${k.top}</b>`)
    out.push(card(r.top.map((t, i) => `${i + 1}. ${tgEscape(t.name)} — ${gel(t.revenue)} · ×${qtyText(t.qty)}`)))
  }
  return out.join('\n')
}

// ── 🔎 a product, by name or barcode ────────────────────────────────────────

export type FoundProduct = {
  name: string
  barcode: string | null
  price: number
  quantity: number
  /** Stock per branch — only when the shop has more than one. */
  branches: { name: string; quantity: number }[]
  /** ISO of the last sale, or null. */
  lastSold: string | null
  sold30: number
}

const S = {
  ka: {
    none: (q: string) => `🔎 „${tgEscape(q)}" — ვერ ვიპოვე.\nდაწერე სახელის ნაწილი ან შტრიხკოდი.`,
    head: (q: string) => `🔎 „${tgEscape(q)}"`,
    ask: 'დაწერე პროდუქტის სახელი ან შტრიხკოდი — ფასს და მარაგს გეტყვი.',
    stock: 'მარაგი', out: 'ამოიწურა', last: (t: string) => `ბოლოს გაიყიდა ${t}`, never: 'ჯერ არ გაყიდულა',
    in30: (n: string) => `30 დღეში ${n}`, more: (n: number) => `და კიდევ ${n} — დააზუსტე სიტყვა`,
  },
  ru: {
    none: (q: string) => `🔎 «${tgEscape(q)}» — не нашёл.\nНапишите часть названия или штрихкод.`,
    head: (q: string) => `🔎 «${tgEscape(q)}»`,
    ask: 'Напишите название товара или штрихкод — скажу цену и остаток.',
    stock: 'Остаток', out: 'нет в наличии', last: (t: string) => `последняя продажа ${t}`, never: 'ещё не продавался',
    in30: (n: string) => `за 30 дней ${n}`, more: (n: number) => `и ещё ${n} — уточните`,
  },
} as const

export const searchAsk = (lang: TgLang) => S[lang].ask

export function searchText(query: string, found: FoundProduct[], more: number, lang: TgLang): string {
  const s = S[lang]
  if (!found.length) return s.none(query)
  const cards = found.map((p) => card([
    `<b>${tgEscape(p.name)}</b> · ${gel(p.price)}`,
    `📦 ${p.quantity > 0.005 ? `${s.stock} <b>${qtyText(p.quantity)}</b>` : `🔴 <b>${s.out}</b>`}${p.branches.length ? ` · ${p.branches.map((b) => `${tgEscape(b.name)} ${qtyText(b.quantity)}`).join(' · ')}` : ''}`,
    `🕒 ${p.lastSold ? s.last(tbilisiStamp(p.lastSold)) : s.never}${p.sold30 > 0 ? ` · ${s.in30(qtyText(p.sold30))}` : ''}`,
    ...(p.barcode ? [`<code>${tgEscape(p.barcode)}</code>`] : []),
  ]))
  return [s.head(query), ...cards, ...(more > 0 ? [`<i>${s.more(more)}</i>`] : [])].join('\n')
}

// ── loss control, as it happens ─────────────────────────────────────────────

/** A cart line removed after the payment window opened is always told; any other removal from this amount up. */
export const LOSS_BIG_REMOVAL = 100
/** A discount from this share of the sale up is told. */
export const LOSS_DISCOUNT_SHARE = 0.1

export type LossEvent =
  | { kind: 'removal'; ref: string; at: string; who: string | null; removal: RemovalKind; lines: { name: string; qty: number }[]; amount: number; afterPayment: boolean; cartTotal: number | null }
  | { kind: 'price'; ref: string; at: string; who: string | null; lines: { name: string; from: number | null; to: number | null }[] }
  | { kind: 'return'; ref: string; at: string; who: string | null; amount: number; paidCash: number; lines: { name: string; qty: number }[] }
  | { kind: 'discount'; ref: string; at: string; who: string | null; discount: number; gross: number }
  | { kind: 'cashout'; ref: string; at: string; who: string | null; amount: number; reason: CashMovementReason; note: string | null }
  | { kind: 'gap'; ref: string; at: string; who: string | null; gap: number; carried: number }

export type LossInput = {
  removals: CartRemoval[]
  priceChanges: PriceChange[]
  /** Returns and discounted sales of the window. */
  sales: Sale[]
  /** Lines of those returns (for the names). */
  // 182: `ingredient_of` set = a ₾0 part the server gave back with a drink — not something handed back
  returnLines: { sale_id: string; product_name: string | null; quantity: number; ingredient_of?: string | null }[]
  /** Money taken out of a drawer, with the cashier of its shift. */
  cashOuts: (CashMovement & { cashier_name?: string | null })[]
  /** Shifts opened in the window. */
  openedShifts: Shift[]
}

/**
 * What the owner is told the minute it happens — the loss-control log (122)
 * and the drawer, filtered to what is worth a look. Nothing here decides who
 * is guilty: a removed line is usually a mis-scan, which is why only the
 * after-payment ones and the big ones are pushed. A price is told when it was
 * changed AT THE TILL (the device's open shift) or by a cashier's login.
 */
export function lossEvents(input: LossInput): LossEvent[] {
  const out: LossEvent[] = []
  for (const g of groupRemovals(input.removals)) {
    if (!g.after_payment && g.amount < LOSS_BIG_REMOVAL) continue
    out.push({
      kind: 'removal', ref: `rm:${g.group_id}`, at: g.at, who: g.cashier_name, removal: g.kind,
      lines: g.rows.map((r) => ({ name: r.product_name, qty: r.quantity })), amount: g.amount, afterPayment: g.after_payment, cartTotal: g.cart_total,
    })
  }
  for (const g of groupPriceChanges(input.priceChanges)) {
    const moved = g.rows.filter((r) => r.old_sale_price !== r.new_sale_price)
    if (!moved.length) continue
    if (!g.rows.some((r) => r.shift_id || r.changed_by_role === 'cashier')) continue
    out.push({ kind: 'price', ref: `pc:${g.txid}`, at: g.at, who: g.rows[0].cashier_name, lines: moved.map((r) => ({ name: r.product_name, from: r.old_sale_price, to: r.new_sale_price })) })
  }
  for (const s of input.sales) {
    if (s.type === 'return') {
      if (s.exchange_id) continue // 1.36 — an exchange's return is not money that left; Telegram tells it as „გაცვლა"
      out.push({
        kind: 'return', ref: `ret:${s.id}`, at: s.created_at, who: s.cashier_name ?? null, amount: s.total, paidCash: s.paid_cash ?? 0,
        lines: input.returnLines.filter((l) => l.sale_id === s.id && l.ingredient_of == null).map((l) => ({ name: l.product_name || '—', qty: l.quantity })),
      })
    } else if ((s.discount ?? 0) > 0.005) {
      const gross = s.total + (s.discount ?? 0)
      if (gross > 0 && (s.discount ?? 0) / gross >= LOSS_DISCOUNT_SHARE) {
        out.push({ kind: 'discount', ref: `disc:${s.id}`, at: s.created_at, who: s.cashier_name ?? null, discount: s.discount ?? 0, gross })
      }
    }
  }
  for (const m of input.cashOuts) {
    if (m.direction !== 'out') continue
    out.push({ kind: 'cashout', ref: `out:${m.id}`, at: m.created_at, who: m.cashier_name ?? null, amount: m.amount, reason: m.reason, note: m.note })
  }
  for (const sh of input.openedShifts) {
    if (sh.opening_carried == null) continue
    const gap = r2(sh.opening_cash - sh.opening_carried)
    if (gap > -0.005) continue
    out.push({ kind: 'gap', ref: `gap:${sh.id}`, at: sh.opened_at, who: sh.cashier_name, gap, carried: sh.opening_carried })
  }
  return out.sort((a, b) => a.at.localeCompare(b.at))
}

const LW = {
  ka: {
    title: 'დანაკარგების კონტროლი',
    removal: { line: 'კალათიდან ამოიღეს', qty: 'რაოდენობა შეამცირეს', clear: 'კალათა გასუფთავდა', held: 'გადადებული კალათა წაიშალა', replaced: 'კალათა გადადებულით ჩაანაცვლეს' } as Record<RemovalKind, string>,
    afterPayment: 'გადახდის ფანჯრის გახსნის შემდეგ', cartWas: (x: string) => `კალათა იყო ${x}`,
    price: 'ფასი შეიცვალა სალაროში', ret: 'დაბრუნება', cashBack: 'ნაღდით დაუბრუნდა', cardBack: 'ბარათზე',
    discount: (pct: string) => `ფასდაკლება ${pct}`, onReceipt: (x: string) => `${x}-იან ჩეკზე`,
    cashout: 'სალაროდან გაიტანეს', reasons: { owner: 'მფლობელმა წაიღო', expense: 'ხარჯი', bank: 'ბანკში შეტანა', change: 'ხურდის დამატება', other: 'სხვა' } as Record<CashMovementReason, string>,
    gap: (x: string) => `ცვლა გაიხსნა ${x}-ით ნაკლებით`, gapWas: (x: string) => `წინამ დატოვა ${x}`,
    more: (n: number) => `და კიდევ ${n} — ბუღალტერია → მოვლენები`, onShift: (n: string) => `${n}-ს ცვლაზე`,
  },
  ru: {
    title: 'Контроль потерь',
    removal: { line: 'Убрали из корзины', qty: 'Уменьшили количество', clear: 'Корзина очищена', held: 'Отложенная корзина удалена', replaced: 'Корзину заменили отложенной' } as Record<RemovalKind, string>,
    afterPayment: 'после открытия окна оплаты', cartWas: (x: string) => `в корзине было ${x}`,
    price: 'Цена изменена на кассе', ret: 'Возврат', cashBack: 'наличными', cardBack: 'на карту',
    discount: (pct: string) => `Скидка ${pct}`, onReceipt: (x: string) => `на чек ${x}`,
    cashout: 'Изъяли из кассы', reasons: { owner: 'владельцу', expense: 'расход', bank: 'в банк', change: 'размен', other: 'другое' } as Record<CashMovementReason, string>,
    gap: (x: string) => `Смена открыта на ${x} меньше`, gapWas: (x: string) => `предыдущая оставила ${x}`,
    more: (n: number) => `и ещё ${n} — Бухгалтерия → События`, onShift: (n: string) => `смена: ${n}`,
  },
} as const

const qtyText = (q: number) => (Number.isInteger(q) ? String(q) : q.toFixed(2))

function lossCard(e: LossEvent, lang: TgLang): string {
  const l = LW[lang]
  const tail = (who: string | null) => [who ? tgEscape(who) : null, tbilisiClock(e.at)].filter(Boolean).join(' · ')
  switch (e.kind) {
    case 'removal': {
      const what = e.lines.length === 1 ? `${tgEscape(e.lines[0].name)} ×${qtyText(e.lines[0].qty)}` : e.lines.slice(0, 3).map((x) => tgEscape(x.name)).join(', ') + (e.lines.length > 3 ? '…' : '')
      return card([
        `${e.afterPayment ? '🔴' : '🛒'} <b>${l.removal[e.removal]}</b> — ${what} · <b>${gel(e.amount)}</b>`,
        [e.afterPayment ? `<b>${l.afterPayment}</b>` : null, e.cartTotal != null ? l.cartWas(gel(e.cartTotal)) : null, tail(e.who)].filter(Boolean).join(' · '),
      ])
    }
    case 'price':
      return card([
        `✏️ <b>${l.price}</b>`,
        ...e.lines.slice(0, 4).map((x) => `${tgEscape(x.name)} ${x.from == null ? '—' : gel(x.from)} → <b>${x.to == null ? '—' : gel(x.to)}</b>${x.from != null && x.to != null ? (x.to < x.from ? ' ⬇️' : ' ⬆️') : ''}`),
        e.who ? `${l.onShift(tgEscape(e.who))} · ${tbilisiClock(e.at)}` : tbilisiClock(e.at),
      ])
    case 'return':
      return card([
        `↩️ <b>${l.ret} ${gel(e.amount)}</b> — ${e.paidCash > 0.005 ? `💵 ${l.cashBack}` : `💳 ${l.cardBack}`}`,
        ...(e.lines.length ? [e.lines.slice(0, 3).map((x) => `${tgEscape(x.name)} ×${qtyText(x.qty)}`).join(', ')] : []),
        tail(e.who),
      ])
    case 'discount':
      return card([`💸 <b>${l.discount(`${Math.round(e.discount / e.gross * 100)}%`)}</b> — ${gel(e.discount)} ${l.onReceipt(gel(e.gross))}`, tail(e.who)])
    case 'cashout':
      return card([`📤 <b>${l.cashout} ${gel(e.amount)}</b> — ${l.reasons[e.reason]}${e.note ? ` · „${tgEscape(e.note)}"` : ''}`, tail(e.who)])
    case 'gap':
      return card([`⚠️ <b>${l.gap(gel(-e.gap))}</b> — ${l.gapWas(gel(e.carried))}`, tail(e.who)])
  }
}

/** One message for everything a minute found in one shop — at most 12 cards. */
export function lossText(shop: string, events: LossEvent[], lang: TgLang): string {
  const l = LW[lang]
  const shown = events.slice(0, 12).map((e) => lossCard(e, lang))
  if (events.length > 12) shown.push(l.more(events.length - 12))
  return [`🚨 <b>${l.title}</b> · ${tgEscape(shop)}`, ...shown].join('\n')
}

// ── the chat's command ──────────────────────────────────────────────────────

export type TgCommand = { cmd: string; arg: string | null }

/** „/status@MartePOSBot" in a group is „/status"; a menu button is its command; anything else is null. */
export function parseCommand(text: string | null | undefined): TgCommand | null {
  const menu = menuCommand(text)
  if (menu) return { cmd: menu, arg: null }
  const t = (text ?? '').trim()
  if (!t.startsWith('/')) return null
  const [head, ...rest] = t.split(/\s+/)
  const cmd = head.toLowerCase().replace(/@[a-z0-9_]+$/i, '')
  // /find takes words; /start takes one code — both are the rest of the line.
  return { cmd, arg: rest.join(' ') || null }
}

/** A one-time code: 16 letters and digits from a CSPRNG — fits Telegram's
 *  deep-link payload ([A-Za-z0-9_-], ≤ 64) and cannot be guessed in 10 minutes. */
export function newLinkCode(random: (n: number) => Uint8Array = (n) => crypto.getRandomValues(new Uint8Array(n))): string {
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789'
  const bytes = random(16)
  let out = ''
  for (const b of bytes) out += alphabet[b % alphabet.length]
  return out
}
